import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Autocomplete,
} from "@mui/material";
// Иконки заменены на текстовые символы для совместимости
import SystemSettings from "src/service/SystemSettings";
import ConfigApi from "src/configs/api";

// Компонент для отображения валюты обменника
const ExchangerCurrencyOption = ({ currency }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Avatar
      src={ConfigApi.PUBLIC_IMAGES + currency.image}
      sx={{ width: 24, height: 24 }}
      alt={currency.name}
    />
    <Box sx={{ ml: 2 }}>
      <Typography variant="body2" fontWeight="medium">
        {currency.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {currency.code}{" "}
        {currency.bestchangeCode && ` (Bestchange:${currency.bestchangeCode} )`}
      </Typography>
    </Box>
  </Box>
);

// Компонент для отображения валюты чейнджера
const ChangerCurrencyOption = ({ currency }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Avatar
      src={currency.logo_url}
      sx={{ width: 24, height: 24 }}
      alt={currency.name}
    />
    <Box sx={{ ml: 2 }}>
      <Typography variant="body2" fontWeight="medium">
        {currency.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {currency.code}
      </Typography>
    </Box>
  </Box>
);

const CurrencyMapping = ({
  onDataChange,
  exchangerCurrencies = [],
  changerCurrencis = [],
}) => {
  const [linkedCurrency, setLinkedCurrency] = useState([]);
  const [newMapping, setNewMapping] = useState({ changer: "", autochange: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await SystemSettings.getAutoChangeSettings();
      const data = response.data;

      if (data.linkedCurrency) {
        setLinkedCurrency(data.linkedCurrency);
      } else {
        setLinkedCurrency([]);
      }
    } catch (err) {
      console.error("Ошибка загрузки настроек автообмена:", err);
      setError("Ошибка загрузки настроек");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMapping = () => {
    if (newMapping.changer.trim() && newMapping.autochange.trim()) {
      const updatedMappings = [...linkedCurrency, { ...newMapping }];
      setLinkedCurrency(updatedMappings);
      setNewMapping({ changer: "", autochange: "" });

      // Уведомляем родительский компонент об изменении
      if (onDataChange) {
        onDataChange({ linkedCurrency: updatedMappings });
      }
    }
  };

  const handleRemoveMapping = (index) => {
    const updatedMappings = linkedCurrency.filter((_, i) => i !== index);
    setLinkedCurrency(updatedMappings);

    // Уведомляем родительский компонент об изменении
    if (onDataChange) {
      onDataChange({ linkedCurrency: updatedMappings });
    }
  };

  const handleInputChange = (field, value) => {
    setNewMapping((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Сопоставление кодов валют"
        subheader="Настройка соответствия между кодами обменников и кодами автообмена"
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Форма добавления нового сопоставления */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Добавить новое сопоставление
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              size="small"
              sx={{ minWidth: 250 }}
              options={exchangerCurrencies}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : `${option.name} (${option.code}) ${
                      option.bestchangeCode ? `[${option.bestchangeCode}]` : ""
                    }`
              }
              value={
                exchangerCurrencies.find(
                  (c) => c.bestchangeCode === newMapping.changer
                ) || null
              }
              onChange={(event, newValue) => {
                handleInputChange(
                  "changer",
                  newValue ? newValue.bestchangeCode : ""
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Валюта обменника"
                  placeholder="Поиск по названию или коду..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <ExchangerCurrencyOption currency={option} />
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                return options.filter(
                  (option) =>
                    option.name
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()) ||
                    option.code
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()) ||
                    (option.bestchangeCode &&
                      option.bestchangeCode
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()))
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.bestchangeCode === value?.bestchangeCode
              }
            />

            <Typography variant="body2" color="text.secondary">
              →
            </Typography>

            <Autocomplete
              size="small"
              sx={{ minWidth: 250 }}
              options={changerCurrencis}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : `${option.name} (${option.code})`
              }
              value={
                changerCurrencis.find(
                  (c) => c.code === newMapping.autochange
                ) || null
              }
              onChange={(event, newValue) => {
                handleInputChange("autochange", newValue ? newValue.code : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Валюта автообмена"
                  placeholder="Поиск по названию или коду..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <ChangerCurrencyOption currency={option} />
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                return options.filter(
                  (option) =>
                    option.name
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()) ||
                    option.code.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
            />

            <Button
              variant="contained"
              onClick={handleAddMapping}
              disabled={
                !newMapping.changer.trim() || !newMapping.autochange.trim()
              }
            >
              + Добавить
            </Button>
          </Box>
        </Box>

        {/* Таблица существующих сопоставлений */}
        {linkedCurrency.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Валюта обменника</TableCell>
                  <TableCell>Валюта автообмена</TableCell>
                  <TableCell align="center">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedCurrency.map((mapping, index) => {
                  const exchangerCurrency = exchangerCurrencies.find(
                    (c) => c.code === mapping.changer
                  );
                  const changerCurrency = changerCurrencis.find(
                    (c) => c.code === mapping.autochange
                  );

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        {exchangerCurrency ? (
                          <ExchangerCurrencyOption
                            currency={exchangerCurrency}
                          />
                        ) : (
                          <Typography variant="body2" fontWeight="medium">
                            {mapping.changer}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {changerCurrency ? (
                          <ChangerCurrencyOption currency={changerCurrency} />
                        ) : (
                          <Typography variant="body2" fontWeight="medium">
                            {mapping.autochange}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveMapping(index)}
                          size="small"
                        >
                          ✕
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              backgroundColor: "grey.50",
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Нет настроенных сопоставлений валют
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrencyMapping;
